import type { GenericVehicleListingSubset } from '@core/Actions/Vehicle/ExtractGenericVehicleListingSubset.action';
import { isWindowDefined } from '@core/Constants/Window';
import type { AllExperiments } from '@growthBookExperimentation/Entities/Experiments.entities';
import type { FlagKey } from '@growthBookExperimentation/Entities/FlagKeys.entities';
import { vehicleToListingEvent } from '@tracking/Schemas/Listing/VehicleToListingEvent';
import { vehicleToListingFeatureEvent } from '@tracking/Schemas/ListingFeature/VehicleToListingFeatureEvent';
import { pdpViewedToEvent } from '@tracking/Schemas/PdpViewed/PdpViewedToEvent';
import type { VehicleDetailsSubset } from '@tracking/Schemas/VehicleDetails/ExtractVehicleDetailsSubset';
import { vehicleToVehicleDetailsEvent } from '@tracking/Schemas/VehicleDetails/VehicleToVehicleDetailsEvent';
import { getExperimentViewedEvents } from '@tracking/Utils/GetExperimentViewedEvents';

export function generatePdpTrackingData(
  genericVehicleListing: GenericVehicleListingSubset,
  vehicleDetailsSubset: VehicleDetailsSubset,
  vehicleId: string,
  isGuaranteeTagVisible: boolean,
  hasReviewData: boolean,
  experiments?: AllExperiments,
  isFirstVisitedPdp?: boolean,
) {
  const experimentIds: FlagKey[] = ['growthbook-test-pdp-experiment', 'usp-pdp-experiment'];

  if (isWindowDefined && window.innerWidth < 768) {
    experimentIds.push('sticky-cta-2-pdp-mobile-experiment');
  }

  const extraData = {
    hasReview: hasReviewData,
    ...(isGuaranteeTagVisible &&
      isWindowDefined &&
      window.innerWidth < 768 && {
        guaranteeName: genericVehicleListing.spec?.guarantee?.name,
      }),
  };

  const listingEvent = vehicleToListingEvent(genericVehicleListing);
  const listingFeatureEvent = vehicleToListingFeatureEvent(
    genericVehicleListing,
    Object.keys(extraData).length ? extraData : undefined,
  );

  const vehicleDetailsEvent = vehicleToVehicleDetailsEvent(vehicleDetailsSubset);

  const pdpViewedEvent = pdpViewedToEvent({ listingId: vehicleId });
  const experimentViewedEvents = getExperimentViewedEvents(experimentIds, experiments);

  const customPageEventContext = [
    listingEvent,
    listingFeatureEvent,
    vehicleDetailsEvent,
    ...(experimentViewedEvents.length > 0 ? [...experimentViewedEvents] : []),
  ];

  let pageViewContext = [listingEvent, listingFeatureEvent, vehicleDetailsEvent];

  if (isFirstVisitedPdp) {
    const pageViewExperimentIds: FlagKey[] = ['autobiz-pdp-viewport-test-location'];

    const experimentViewedEvents = getExperimentViewedEvents(pageViewExperimentIds, experiments);
    pageViewContext = [
      ...pageViewContext,
      ...(experimentViewedEvents.length > 0 ? [...experimentViewedEvents] : []),
    ];
  }

  return {
    pdpEvents: {
      event: pdpViewedEvent,
      context: pageViewContext,
      customPageEventContext: customPageEventContext,
    },
  };
}
